<template>
  <teleport to="body">
    <div class="cm-fondo" @click.self="cerrar">
      <article class="cm-caja" role="dialog" aria-modal="true" aria-labelledby="cm-ed-titulo"
               data-test="modal-editar-concepto">
        <header class="vx-panel-cabecera">
          <div>
            <h2 id="cm-ed-titulo" class="vx-panel-titulo">Editar concepto</h2>
            <div class="vx-ayuda mt-1">
              <template v-if="concepto.emitidas">
                Ya generó {{ concepto.emitidas }}
                {{ concepto.emitidas === 1 ? 'cuota' : 'cuotas' }}: las que no se cobraron
                se recalculan. Las cobradas no se tocan.
              </template>
              <template v-else>Todavía no generó cuotas: se corrige entero.</template>
            </div>
            <!-- RN-182: editar un desactivado lo reactiva. Se dice ANTES de guardar. -->
            <div v-if="concepto.activo === false" class="vx-ayuda mt-1" data-test="ce-reactiva">
              <i class="bi bi-play-circle"></i>
              Está desactivado: al guardar vuelve a estar activo.
            </div>
          </div>
          <button class="vx-btn vx-btn-sm" @click="cerrar">Cancelar</button>
        </header>

        <div class="p-3">
          <div class="row g-2">
            <div class="col-md-6">
              <label class="vx-etiqueta" for="ce-descripcion">Descripción</label>
              <input id="ce-descripcion" v-model.trim="form.descripcion" class="vx-campo vx-campo-sm"
                     data-test="ce-descripcion" @input="olvidarPropuesta">
            </div>
            <div class="col-md-3">
              <label class="vx-etiqueta" for="ce-monto">Monto</label>
              <input id="ce-monto" v-model.number="form.monto" type="number" min="0"
                     class="vx-campo vx-campo-sm vx-num" data-test="ce-monto" @input="olvidarPropuesta">
            </div>
            <div class="col-md-3">
              <label class="vx-etiqueta" for="ce-iva">IVA</label>
              <select id="ce-iva" v-model.number="form.tipo_iva" class="vx-campo vx-campo-sm"
                      data-test="ce-iva" @change="olvidarPropuesta">
                <option v-for="t in TIPOS_IVA" :key="t.valor" :value="t.valor">{{ t.texto }}</option>
              </select>
            </div>
            <!-- RN-158: el tipo sólo cambia sin cuotas, y nunca hacia o desde
                 un cargo eventual. -->
            <div v-if="tipoEditable" class="col-md-6">
              <label class="vx-etiqueta" for="ce-tipo">Tipo</label>
              <select id="ce-tipo" v-model="form.tipo" class="vx-campo vx-campo-sm">
                <option value="RECURRENTE">Recurrente</option>
                <option value="UNICO">Único</option>
              </select>
            </div>
          </div>

          <!-- RN-161: qué cambia, antes de que cambie. -->
          <div v-if="propuesta" class="mt-3 pt-3 border-top" data-test="propuesta-edicion">
            <div class="d-flex justify-content-between align-items-center mb-2">
              <strong style="font-size: 13.5px">
                {{ aAplicar }} {{ aAplicar === 1 ? 'cuota cambia' : 'cuotas cambian' }}
              </strong>
              <span class="vx-num" data-test="diferencia-edicion"
                    :style="{ fontWeight: 600, color: diferencia > 0 ? '#b91c1c' : '#15803d' }">
                {{ diferencia > 0 ? '+' : '' }}{{ formatearMonto(diferencia, moneda) }}
              </span>
            </div>

            <div v-if="!propuesta.al_dia.length && !propuesta.vencidas.length" class="vx-ayuda">
              Ninguna cuota cambia: las que hay están cobradas o anuladas.
            </div>

            <div class="cm-lista">
              <div v-for="c in propuesta.al_dia" :key="c.cr_deuda_id"
                   class="d-flex justify-content-between gap-2 py-1">
                <span class="vx-celda-sub">{{ c.periodo }} · {{ c.nombre }}</span>
                <span class="vx-num" style="font-size: 12.5px">
                  {{ formatearMonto(c.monto_total_actual, moneda) }} →
                  <strong>{{ formatearMonto(c.monto_total_nuevo, moneda) }}</strong>
                </span>
              </div>
            </div>

            <!-- RN-162: las vencidas, aparte y apagadas por defecto. -->
            <div v-if="propuesta.vencidas.length" class="mt-2 pt-2 border-top">
              <label class="d-flex align-items-start gap-2" style="cursor: pointer">
                <input v-model="incluirVencidas" type="checkbox" class="mt-1"
                       data-test="ce-incluir-vencidas" @change="previsualizar">
                <span class="vx-ayuda">
                  Incluir {{ propuesta.vencidas.length }}
                  {{ propuesta.vencidas.length === 1 ? 'cuota vencida' : 'cuotas vencidas' }}
                  sin cobrar. A esas familias ya se les comunicó un importe.
                </span>
              </label>
            </div>

            <div v-if="propuesta.intactas.length" class="mt-2 pt-2 border-top vx-ayuda"
                 data-test="ce-intactas">
              <template v-if="propuesta.intactas.length === 1">
                No se toca 1 cuota: ya tiene un pago aplicado o está anulada.
              </template>
              <template v-else>
                No se tocan {{ propuesta.intactas.length }} cuotas: ya tienen un pago aplicado
                o están anuladas.
              </template>
            </div>

            <label class="vx-etiqueta mt-3" for="ce-motivo">Motivo (opcional)</label>
            <input id="ce-motivo" v-model.trim="motivo" class="vx-campo vx-campo-sm"
                   placeholder="Ej.: ajuste de precio de julio" data-test="ce-motivo">
          </div>

          <div v-if="error" class="vx-error mt-2" data-test="ce-error">{{ error }}</div>

          <div class="d-flex justify-content-end gap-2 mt-3">
            <button
              v-if="concepto.emitidas && !propuesta"
              class="vx-btn vx-btn-sm vx-btn-primario" :disabled="trabajando"
              data-test="ce-ver-cambios" @click="previsualizar"
            >
              <span v-if="trabajando" class="spinner-border spinner-border-sm"></span>
              Ver qué cambia
            </button>
            <button
              v-else
              class="vx-btn vx-btn-sm vx-btn-primario" :disabled="trabajando"
              data-test="ce-confirmar" @click="confirmar"
            >
              <span v-if="trabajando" class="spinner-border spinner-border-sm"></span>
              {{ textoConfirmar }}
            </button>
          </div>
        </div>
      </article>
    </div>
  </teleport>
</template>

<script setup>
/* global defineProps, defineEmits */
// Editar un concepto — ZC-F5-003. SPEC_EDICION_DE_CONCEPTOS RN-158 a RN-162.
import { ref, reactive, computed } from 'vue'
import {
  TIPOS_IVA, formatearMonto, previsualizarEdicionConcepto, confirmarEdicionConcepto
} from '@/services/cobranzaService'
import { cuotasAAplicar } from '@/utils/conceptoAcciones.mjs'

const props = defineProps({
  concepto: { type: Object, required: true },
  moneda: { type: String, default: 'PYG' }
})
const emit = defineEmits(['cerrar', 'guardado'])

const form = reactive({
  descripcion: props.concepto.descripcion,
  monto: Number(props.concepto.monto),
  tipo_iva: Number(props.concepto.tipo_iva),
  tipo: props.concepto.tipo
})
const propuesta = ref(null)
const incluirVencidas = ref(false)
const motivo = ref('')
const trabajando = ref(false)
const error = ref('')

const textoConfirmar = computed(() => {
  const base = props.concepto.emitidas ? 'Confirmar' : 'Guardar'
  return props.concepto.activo === false ? `${base} y reactivar` : base
})
const tipoEditable = computed(() => !props.concepto.emitidas && props.concepto.tipo !== 'EVENTUAL')
const aAplicar = computed(() => cuotasAAplicar(propuesta.value, incluirVencidas.value))
const diferencia = computed(() => {
  if (!propuesta.value) return 0
  const filas = [...propuesta.value.al_dia, ...(incluirVencidas.value ? propuesta.value.vencidas : [])]
  return filas.reduce((acc, c) => acc + Number(c.diferencia), 0)
})

const cuerpo = () => ({
  descripcion: form.descripcion,
  monto: form.monto,
  tipo_iva: form.tipo_iva,
  ...(tipoEditable.value ? { tipo: form.tipo } : {}),
  incluir_vencidas: incluirVencidas.value
})

// Cambiar el formulario invalida lo que se mostró: confirmar sobre una
// previsualización vieja haría algo distinto de lo que el operador vio.
function olvidarPropuesta () {
  propuesta.value = null
  incluirVencidas.value = false
}

async function previsualizar () {
  error.value = ''
  trabajando.value = true
  try {
    propuesta.value = await previsualizarEdicionConcepto(props.concepto, cuerpo())
  } catch (err) {
    error.value = err?.userMessage || 'No se pudo calcular el cambio.'
  } finally {
    trabajando.value = false
  }
}

async function confirmar () {
  error.value = ''
  trabajando.value = true
  try {
    const r = await confirmarEdicionConcepto(props.concepto, { ...cuerpo(), motivo: motivo.value })
    emit('guardado', r)
  } catch (err) {
    error.value = err?.userMessage || 'No se pudo guardar el concepto.'
  } finally {
    trabajando.value = false
  }
}

const cerrar = () => emit('cerrar')
</script>

<style scoped>
.cm-fondo {
  position: fixed; inset: 0; z-index: 2100;
  display: flex; align-items: center; justify-content: center;
  padding: 16px;
  background: rgba(15, 23, 42, 0.35);
}
.cm-caja {
  width: min(620px, 100%);
  max-height: calc(100vh - 32px);
  overflow-y: auto;
  background: var(--vx-panel);
  border-radius: var(--vx-radio);
  box-shadow: 0 22px 46px rgba(15, 23, 42, 0.2);
}
.cm-lista { max-height: 220px; overflow-y: auto; }
</style>
